import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Spinner } from '@/components/shared/UIComponents';
import { AuthHero } from '@/components/auth/AuthHero';
import { AuthFooter } from '@/components/auth/AuthFooter';

export function LoginPage() {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }
    setLoading(true);
    try {
      await signIn(email, password);
      navigate('/admin');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-50 flex flex-col lg:flex-row">
      <AuthHero
        headline={<>Manage your white-label<br />travel platform</>}
        subheadline="Configure tenants, customise branding, manage features, and monitor your entire SaaS platform from one place."
      />

      {/* Right panel — form + footer stacked */}
      <div className="flex-1 flex flex-col">
        <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-3 mb-10">
            <img src="/logo.svg" alt="DiscoveryGem" className="w-9 h-9 dark:brightness-0 dark:invert" />
            <div>
              <div className="font-semibold text-surface-900">DiscoveryGem</div>
              <div className="text-[10px] text-surface-500 uppercase tracking-widest">Platform</div>
            </div>
          </div>

          <h1 className="text-2xl font-bold text-surface-900 mb-2">Welcome back</h1>
          <p className="text-sm text-surface-500 mb-8">Sign in to your admin account</p>

          {error && (
            <div className="mb-6 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-sm text-red-600 dark:text-red-400">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="input-label">Email</label>
              <input type="email" className="input-field" placeholder="admin@company.com"
                value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" autoFocus />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-surface-700">Password</label>
                <Link to="/auth/forgot-password" className="text-xs text-brand-600 hover:text-brand-700 transition-colors">
                  Forgot password?
                </Link>
              </div>
              <input type="password" className="input-field" placeholder="••••••••"
                value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
            </div>
            <button type="submit" disabled={loading} className="btn-primary w-full py-3">
              {loading ? <Spinner size="sm" /> : 'Sign In'}
            </button>
          </form>

          {/* Platform is sales-led — Pro/Business/Enterprise plans are
              custom-quoted per tenant rather than self-serve, so the
              primary "no account yet" CTA is a demo request rather
              than a signup link. The Signup page itself stays wired
              at /auth/signup for direct-invite links sent after a
              demo call closes. */}
          <p className="text-sm text-surface-500 text-center mt-8">
            Don't have an account?{' '}
            <a
              href="mailto:partners@discoverygem.com?subject=Platform%20demo%20request"
              className="text-brand-600 hover:text-brand-700 font-medium transition-colors"
            >
              Request a demo
            </a>
          </p>
        </div>
        </div>
        <AuthFooter />
      </div>
    </div>
  );
}
